import type {ReactElement} from 'react';
import React from 'react';
import {AudioWaveform} from '@Components/audio-waveform';
import {ITEM_TRACK_HEIGHT, TRACK_GAP} from '../../timeline.types';
import styles from './trim-ghost.module.scss';
import type {AudioItemObject} from "@PosterWhiteboard/classes/audio-clips/audio-item.class";

interface TrimGhostProps {
  id: string;
  audioItemData: AudioItemObject;
  startTime: number;
  subRow: number;
  widthPerSecond: number;
  isVisible: boolean;
}

/**
 * Shows a faded ghost of the full untrimmed audio when selected.
 * Helps users visualize how much of the audio is trimmed.
 * Always mounted but hidden when not visible to preserve WaveSurfer instance.
 */
export function TrimGhost({id, audioItemData, startTime, subRow, widthPerSecond, isVisible}: TrimGhostProps): ReactElement {
  const {speed, originalDuration, trim} = audioItemData.audioPlayer;
  const fullDurationOnTimeline = originalDuration / speed;
  const ghostWidth = fullDurationOnTimeline * widthPerSecond;
  const ghostLeft = (startTime - trim.startTime / speed) * widthPerSecond;

  return (
    <div
      className={styles.trimGhost}
      style={{
        left: `${ghostLeft}px`,
        width: `${ghostWidth}px`,
        top: `${subRow * (ITEM_TRACK_HEIGHT + TRACK_GAP)}px`,
        height: `${ITEM_TRACK_HEIGHT}px`,
        visibility: isVisible ? 'visible' : 'hidden',
      }}
      aria-hidden="true"
    >
      <AudioWaveform
        style={{
          background: 'transparent',
          top: 0,
          left: 0,
          width: `${ghostWidth}px`,
        }}
        doShowLoader={false}
        additionalClasses={`${styles.waveform} ${styles.trimGhostWaveform}`}
        waveSurferProps={{
          backgroundColor: 'transparent',
          waveColor: '#888',
          barWidth: undefined,
          barRadius: undefined,
          barMinHeight: undefined,
          barGap: undefined,
          height: ITEM_TRACK_HEIGHT - 8,
          audioRate: speed,
          cursorWidth: 0,
        }}
        id={`audiowaveform-ghost-${id}`}
        key={`audiowaveform-ghost-${id}-${speed}`}
        url={audioItemData.audioPlayer.audioUrl}
        isPlaying={false}
        endTime={originalDuration}
        seek={0}
        duration={originalDuration}
        pxPerSec={widthPerSecond}
      />
    </div>
  );
}

